import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * TABLE
 *
 * Most screens in this product are a list of things — calls, drivers, impounds,
 * invoices — so the table carries more weight than any other component here.
 *
 * Two decisions worth knowing about:
 *   • One density, not two. `density` used to be a variant, on the theory that
 *     a settings table wants room to breathe while a call log wants rows. In
 *     practice 14 of 25 tables were comfortable only because that was the
 *     default — none of them chose it — and two tables in the same feature
 *     disagreed. Padding never constrains content anyway: a cell holding a
 *     32px button is 44px tall either way. So the scanning density won.
 *   • The header is sticky by default. A table that scrolls its column labels
 *     off the top is unusable at the row counts these screens hit.
 */
export const tableRecipe = cva([uiMarker, 'w-full caption-bottom font-sans'], {
  variants: {
    /** Fixed layout stops columns jumping as data loads. */
    layout: {
      auto: 'table-auto',
      fixed: 'table-fixed',
    },
    /**
     * `grid` is the ruled table of the settings and records screens. `band`
     * is the Table view of a grouped list (Figma 25-3408), drawn the way the
     * list beside it is: a rounded header band, no rules, 16px rows and a
     * rounded tint on the row under the pointer. Separate borders, because a
     * collapsed table cannot round a row's ends.
     */
    shape: {
      grid: 'border-collapse text-body-sm',
      band: 'border-separate border-spacing-0 text-body-lg',
    },
  },
  defaultVariants: { layout: 'auto', shape: 'grid' },
});

export type TableRecipeProps = VariantProps<typeof tableRecipe>;
export type TableShape = NonNullable<TableRecipeProps['shape']>;

/** Scroll container. A table wider than the screen scrolls itself, not the page. */
export const tableScrollRecipe = cva(
  [uiMarker, uiMarkerLayout, 'w-full overflow-x-auto'],
  {
    variants: {
      bordered: {
        true: 'rounded-xl border border-border',
        false: '',
      },
      /**
       * A band table carries the list column's 16px inset, as the toolbar
       * and the bands do, and has no frame: the header band is its edge. It
       * starts 12 under the toolbar, as a ListBand does, so switching from
       * List to Table leaves the header where the first band was; without
       * it the table jumped up the screen on the switch.
       */
      shape: {
        grid: '',
        band: 'px-4 pt-3',
      },
    },
    compoundVariants: [
      { shape: 'band', bordered: true, class: 'rounded-none border-0' },
    ],
    defaultVariants: { bordered: true, shape: 'grid' },
  }
);

export const tableHeaderRecipe = cva([uiMarker, uiMarkerLayout], {
  variants: {
    sticky: {
      true: 'sticky top-0 z-10 backdrop-blur',
      false: '',
    },
    /** A band table's fill is on its header cells, so the band can round. */
    shape: {
      grid: 'bg-muted/50',
      band: 'bg-background',
    },
  },
  defaultVariants: { sticky: true, shape: 'grid' },
});

export const tableHeadRecipe = cva(
  [
    uiMarker,
    'text-left align-middle font-sans text-label text-muted-foreground',
  ],
  {
    variants: {
      /**
       * `grid`: 32px, matching a body row (6 + 19 + 6), a fixed height
       * rather than the cells' padding so the header stays put whatever a
       * label wraps to. `band`: the 40px band the design draws, its fill on
       * the cells and its ends rounded, 8 in from each end as the rows are.
       */
      shape: {
        grid: 'h-8 border-b border-border px-3',
        band: 'h-10 whitespace-nowrap bg-muted/50 px-2 first:rounded-l-lg last:rounded-r-lg',
      },
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
      /** Column headers that sort are interactive and must look it. */
      sortable: {
        true: 'cursor-pointer select-none transition-colors duration-fast hover:text-foreground',
        false: '',
      },
      /**
       * Matches the cells below it. A numeric column whose header stays
       * left-aligned reads as a different column from its own values.
       */
      numeric: {
        true: 'text-right',
        false: '',
      },
      /**
       * A column that only fits from a breakpoint up — the category or the
       * payment method, which a phone has no room for. Set the same value on
       * the column's TableHead and each TableCell. Written out per breakpoint
       * because Tailwind reads class names from source.
       */
      hideBelow: {
        none: '',
        sm: 'hidden sm:table-cell',
        md: 'hidden md:table-cell',
        lg: 'hidden lg:table-cell',
      },
      /**
       * The column that stays put while the rest scroll under it — the
       * row-action column at the right edge of a wide import table. Opaque,
       * with a rule on its inner edge, so the scrolled columns disappear
       * behind it rather than showing through. Set the same value on the
       * column's TableHead and each TableCell.
       */
      pinned: {
        none: '',
        end: 'sticky right-0 z-10 border-l border-border bg-background',
      },
    },
    compoundVariants: [
      // The sort control is inline-flex, so the cell's text-align does not move
      // it — it has to be pushed over itself.
      { numeric: true, sortable: true, class: '[&>button]:ml-auto' },
    ],
    defaultVariants: {
      shape: 'grid',
      align: 'left',
      sortable: false,
      numeric: false,
      hideBelow: 'none',
      pinned: 'none',
    },
  }
);

export const tableRowRecipe = cva(
  [uiMarker, uiMarkerLayout, 'transition-colors duration-fast'],
  {
    variants: {
      shape: {
        grid: 'border-b border-border last:border-0',
        /**
         * The tint is on the cells, so the row's ends can round. The first
         * column is what the row is, an impound's number, at the strong
         * weight; a link in a cell reads as that text until it is pointed
         * at or focused, since the whole row opens the record anyway.
         */
        band: [
          '[&>td:first-child]:rounded-l-lg [&>td:last-child]:rounded-r-lg',
          '[&>td:first-child]:font-medium',
          '[&_a]:no-underline [&_a:focus-visible]:underline [&_a:hover]:underline',
        ].join(' '),
      },
      interactive: {
        /*
         * A row a keyboard can open (ColumnsTable makes it focusable) shows
         * focus as it shows hover, plus an outline: a ring is a box-shadow,
         * which a table row does not draw.
         */
        true: 'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring',
        false: '',
      },
      selected: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        shape: 'grid',
        interactive: true,
        class: 'hover:bg-muted/60 focus-visible:bg-muted/60',
      },
      { shape: 'grid', selected: true, class: 'bg-info-subtle/60' },
      // The design's green, on the cells: a band row is tinted where the
      // list's would be grey, so the table reads as the list's other view
      // without the two being mistaken for each other.
      // The hover is the row's, written as one selector: Tailwind stacks
      // `hover:[&>td]:` inside out, as `& > td:hover`, which tinted only the
      // cell under the pointer.
      {
        shape: 'band',
        interactive: true,
        class:
          '[&:hover>td]:bg-success-subtle [&:focus-visible>td]:bg-success-subtle',
      },
      { shape: 'band', selected: true, class: '[&>td]:bg-success-subtle' },
    ],
    defaultVariants: { shape: 'grid', interactive: false, selected: false },
  }
);

export const tableCellRecipe = cva([uiMarker, 'align-middle'], {
  variants: {
    /**
     * `band`: 43 high at 16px (12 + 19 + 12), 8 in from the band's ends,
     * and each column at least the design's 148 so a phone scrolls the
     * table sideways rather than wrapping a vehicle onto three lines.
     */
    shape: {
      grid: 'px-3 py-1.5',
      band: 'min-w-[148px] whitespace-nowrap px-2 py-3',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    /** Right-align and tabular-align money and counts so digits stack. */
    numeric: {
      true: 'text-right tabular-nums',
      false: '',
    },
    truncate: {
      true: 'max-w-0 truncate',
      false: '',
    },
    /** Matches the TableHead above it; see there. */
    hideBelow: {
      none: '',
      sm: 'hidden sm:table-cell',
      md: 'hidden md:table-cell',
      lg: 'hidden lg:table-cell',
    },
    /**
     * Matches the TableHead above it; see there. No z-index: a positioned
     * cell already paints over the static cells beside it, and giving it the
     * header's z-10 let scrolled body rows cover the sticky header.
     */
    pinned: {
      none: '',
      end: 'sticky right-0 border-l border-border bg-background',
    },
    /**
     * A cell whose value failed validation — a row of the bulk import grid
     * with a date that would not parse. Drawn as a box rather than a tint
     * alone because the cells around it are the same colour and a stain
     * with no edge does not say *which* one.
     */
    invalid: {
      true: 'border-2 border-destructive bg-destructive-subtle',
      false: '',
    },
  },
  defaultVariants: {
    shape: 'grid',
    align: 'left',
    numeric: false,
    truncate: false,
    hideBelow: 'none',
    pinned: 'none',
    invalid: false,
  },
});

/** The "nothing here yet" row, spanning the full width of the table. */
export const tableEmptyRecipe = cva([
  uiMarker,
  'px-4 py-12 text-center font-sans text-body text-muted-foreground',
]);
